/**
 * AgenciasLayout — Sidebar secundaria (desktop) / Tabs scrolleables (mobile)
 * para alternar entre la lista de Agencias y la gestión de Comisiones.
 * Mismo patrón que CatalogoLayout / BookingLayout / PasadiasLayout / MuelleLayout.
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Tabs, Tab,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';

// "Comisiones" se movió al módulo Finanzas (/finanzas/comisiones). Agencias
// queda como CRM puro: lista de revendedores y solicitudes de actualización.
const SECTIONS = [
  { path: 'lista',        label: 'Agencias',     icon: <BusinessIcon />, color: '#be185d' },
  { path: 'solicitudes',  label: 'Solicitudes',  icon: <EditIcon />,     color: '#0284c7' },
];

export default function AgenciasLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  // Redirigir a "lista" si entran a /agencias sin sub-ruta
  useEffect(() => {
    if (location.pathname === '/agencias' || location.pathname === '/agencias/') {
      navigate('/agencias/lista', { replace: true });
    }
  }, [location.pathname, navigate]);

  const activeIdx = Math.max(0, SECTIONS.findIndex(s => location.pathname.includes(`/agencias/${s.path}`)));

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar secundario (desktop) */}
      <Box sx={{
        width: 240, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Agencias
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0 }} dense>
          {SECTIONS.map(s => {
            const isActive = location.pathname.includes(`/agencias/${s.path}`);
            return (
              <ListItemButton key={s.path} onClick={() => navigate(`/agencias/${s.path}`)}
                sx={{
                  borderRadius: 2, mb: 0.5, py: 0.75, pl: 1.5,
                  bgcolor: isActive ? `${s.color}15` : 'transparent',
                  borderLeft: isActive ? `3px solid ${s.color}` : '3px solid transparent',
                  '&:hover': { bgcolor: isActive ? `${s.color}25` : '#f1f5f9' },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: isActive ? s.color : '#94a3b8' }}>
                  {s.icon}
                </ListItemIcon>
                <ListItemText primary={s.label}
                  primaryTypographyProps={{
                    fontSize: 13, fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#0f172a' : '#475569',
                  }} />
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }} />
        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Gestión de revendedores y solicitudes. Para comisiones y liquidaciones, ver Finanzas.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Tabs móviles */}
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          position: 'sticky', top: 0, zIndex: 5,
        }}>
          <Tabs
            value={activeIdx}
            onChange={(_, idx) => navigate(`/agencias/${SECTIONS[idx].path}`)}
            variant="fullWidth"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 700, fontSize: 13 },
              '& .MuiTabs-indicator': { backgroundColor: SECTIONS[activeIdx]?.color || '#be185d', height: 3, borderRadius: 2 },
            }}
          >
            {SECTIONS.map(s => (
              <Tab key={s.path} icon={s.icon} iconPosition="start" label={s.label} />
            ))}
          </Tabs>
        </Box>

        <Outlet />
      </Box>
    </Box>
  );
}
